import {
  acquireLocalWithdrawalSyncLease,
  compareAndSwapLocalWithdrawal,
  createLocalSyncLeaseOwner,
  getLocalWithdrawal,
  LocalWithdrawalRevisionConflictError,
  releaseLocalWithdrawalSyncLease,
  renewLocalWithdrawalSyncLease,
} from "../storage/offlineDatabase";
import type { LocalWithdrawalData, LocalWithdrawalRecord } from "../types/offlineWithdrawal";
import { getLastKnownAuthUser } from "./authService";
import { nextLocalWithdrawalNumber, rememberConfirmedWithdrawalNumber } from "./withdrawalIdentityService";
import { uploadWithdrawalFile, uploadWithdrawalFileRaw } from "../api/sync";
import { registerMaterialentnahmeBackgroundSync, updateMaterialentnahmeAppBadge } from "./serviceWorkerService";
import { WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION } from "./withdrawalDocumentConstants";
import { dispatchLocalWithdrawalUpdated } from "./localWithdrawalEvents";
import { classifyRequestFailure, fetchWithTimeout, HttpError } from "../utils/fetchWithTimeout";
import { verifyServerArchiveAndCompact } from "./localStorageLifecycleService";

export interface DraftSaveResult {
  record: LocalWithdrawalRecord;
  synced: boolean;
}

const DRAFT_SYNC_MAX_ATTEMPTS = 9;
const DRAFT_SYNC_RETRY_BASE_DELAY_MS = 30_000;
const DRAFT_SYNC_RETRY_MAX_DELAY_MS = 15 * 60_000;
const DRAFT_SYNC_LEASE_HEARTBEAT_MS = 60_000;
const activeDraftSyncs = new Map<string, Promise<LocalWithdrawalRecord>>();

function draftRetryDelayMs(attempt: number): number {
  const exponent = Math.max(0, Math.min(5, attempt - 1));
  const base = Math.min(DRAFT_SYNC_RETRY_MAX_DELAY_MS, DRAFT_SYNC_RETRY_BASE_DELAY_MS * (2 ** exponent));
  return Math.min(DRAFT_SYNC_RETRY_MAX_DELAY_MS, Math.round(base * (0.85 + Math.random() * 0.30)));
}

function isTerminalDraftError(error: unknown): boolean {
  return error instanceof HttpError
    && (error.status === 400 || error.status === 401 || error.status === 403 || error.status === 409 || error.status === 422);
}


async function updateDraftAtRevision(
  clientUuid: string,
  expectedRevision: number,
  updater: (current: LocalWithdrawalRecord) => LocalWithdrawalRecord,
): Promise<LocalWithdrawalRecord | null> {
  try {
    return await compareAndSwapLocalWithdrawal(clientUuid, expectedRevision, (current) => {
      if (!current || current.record_type !== "draft") return current;
      return updater(current);
    });
  } catch (error) {
    if (error instanceof LocalWithdrawalRevisionConflictError) {
      return getLocalWithdrawal(clientUuid);
    }
    throw error;
  }
}

function draftDocumentDataFromRecord(record: LocalWithdrawalRecord) {
  const data = record.data;
  return {
    withdrawalDate: data.withdrawal_date,
    materialNumber: data.material_number,
    materialText: data.material_text,
    materialType: data.material_type ?? "",
    storageAddress: [data.building, data.floor, data.cabinet, data.compartment, data.sub_compartment].filter(Boolean).join(" "),
    quantityRemoved: data.quantity_removed,
    currentStock: data.current_stock,
    persons: data.persons.map((person) => person.name),
    accountingNumber: data.accounting_number ?? "",
    accountingDescription: [data.accounting_system, data.accounting_subarea].filter(Boolean).join(" - "),
    orderInfo: data.order_info ?? "",
  };
}

async function ensureDraftDocumentsBeforeSync(record: LocalWithdrawalRecord): Promise<LocalWithdrawalRecord> {
  const complete = Boolean(record.pdf_blob && record.pdf_filename && record.pdf_preview_blob && record.pdf_preview_filename);
  if (complete && record.document_artifact_version === WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION) return record;
  if (!record.data.withdrawal_date) throw new Error("Entnahmedatum fehlt. Der Materialentnahmeschein kann lokal nicht neu erzeugt werden.");

  // Schweres PDF/PDF.js nur laden, wenn ein unterbrochener Zwei-Phasen-Save
  // tatsächlich noch keine Pflichtdokumente besitzt. Dadurch bleibt der normale
  // App-Start schlank, während ein Browser-/App-Abbruch zwischen Phase 1 und 2
  // trotzdem vollständig selbstheilend bleibt und keinen Sync-Attempt verbraucht.
  const { createWithdrawalDocumentArtifacts } = await import("./withdrawalDocumentService");
  const artifacts = await createWithdrawalDocumentArtifacts(draftDocumentDataFromRecord(record));
  const safeMaterial = record.data.material_number || "Entwurf";
  const artifactDate = record.data.withdrawal_date;
  const updated = await updateDraftDocumentArtifacts(record.client_uuid, Math.max(0, record.content_revision ?? 0), {
    pdfBlob: artifacts.pdf,
    pdfFilename: record.pdf_filename ?? `${safeMaterial}_${artifactDate}.pdf`,
    pdfPreviewBlob: artifacts.preview,
    pdfPreviewFilename: record.pdf_preview_filename ?? `${safeMaterial}_${artifactDate}_Vorschau.webp`,
  });
  if (!updated || updated.record_type !== "draft") return updated ?? record;
  return updated;
}

function isDraftConnectivityError(error: unknown): boolean {
  const kind = classifyRequestFailure(error);
  if (kind === "timeout" || kind === "network" || kind === "aborted") return true;
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? "");
  return /serververbindung.*nicht verfügbar|server nicht erreichbar/i.test(message);
}

function draftConnectivityMessage(error: unknown): string {
  return classifyRequestFailure(error) === "timeout"
    ? "Serverantwort derzeit zu langsam. Der Entwurf bleibt lokal gespeichert und wird später automatisch synchronisiert."
    : "Serververbindung derzeit nicht verfügbar. Der Entwurf bleibt lokal gespeichert und wird später automatisch synchronisiert.";
}

export interface DraftArtifacts {
  pdfBlob?: Blob;
  pdfFilename?: string;
  pdfPreviewBlob?: Blob;
  pdfPreviewFilename?: string;
  labelBlob?: Blob | null;
  labelFilename?: string | null;
  sourceBlob?: Blob | null;
  sourceFilename?: string | null;
  detectionSourceBlob?: Blob | null;
  detectionSourceFilename?: string | null;
  analysisInputBlob?: Blob | null;
  analysisInputFilename?: string | null;
  rectificationInputBlob?: Blob | null;
  rectificationInputFilename?: string | null;
  ocrWarpBlob?: Blob | null;
  ocrWarpFilename?: string | null;
  visionFeedbackBlob?: Blob | null;
  visionFeedbackFilename?: string | null;
  liveTrainingBlob?: Blob | null;
  liveTrainingFilename?: string | null;
  debugImageBlob?: Blob | null;
  debugImageFilename?: string | null;
  debugDataBlob?: Blob | null;
  debugDataFilename?: string | null;
}

async function uploadDraftArtifacts(record: LocalWithdrawalRecord): Promise<void> {
  if (!record.server_withdrawal_id) return;
  if (!record.pdf_blob || !record.pdf_filename || !record.pdf_preview_blob || !record.pdf_preview_filename) {
    throw new Error("Materialentnahmeschein oder Vorschau fehlt. Der Entwurf bleibt ausstehend, bis beide Pflichtdokumente vorhanden sind.");
  }
  const id = record.server_withdrawal_id;
  if (record.pdf_blob && record.pdf_filename) await uploadWithdrawalFile(id, "pdf", record.pdf_blob, record.pdf_filename);
  if (record.pdf_preview_blob && record.pdf_preview_filename) {
    await uploadWithdrawalFile(id, "pdf_preview", record.pdf_preview_blob, record.pdf_preview_filename);
  }
  // v0.256.0: Manual withdrawals are business records only. Never upload Vision/VO/debug artifacts.
  if ((record.data.capture_mode ?? "automatic") === "manual") return;
  if (record.label_blob && record.label_filename) {
    await uploadWithdrawalFileRaw(id, "label", record.label_blob, record.label_filename).catch(() => undefined);
  }
  if (record.source_blob && record.source_filename) {
    await uploadWithdrawalFileRaw(id, "source", record.source_blob, record.source_filename).catch(() => undefined);
  }
  if (record.detection_source_blob && record.detection_source_filename) {
    await uploadWithdrawalFileRaw(id, "detection_source", record.detection_source_blob, record.detection_source_filename).catch(() => undefined);
  }
  if (record.analysis_input_blob && record.analysis_input_filename) {
    await uploadWithdrawalFileRaw(id, "analysis_input", record.analysis_input_blob, record.analysis_input_filename).catch(() => undefined);
  }
  if (record.rectification_input_blob && record.rectification_input_filename) {
    await uploadWithdrawalFileRaw(id, "rectification_input", record.rectification_input_blob, record.rectification_input_filename).catch(() => undefined);
  }
  if (record.ocr_warp_blob && record.ocr_warp_filename) {
    await uploadWithdrawalFileRaw(id, "ocr_warp", record.ocr_warp_blob, record.ocr_warp_filename).catch(() => undefined);
  }
  if (record.vision_feedback_blob && record.vision_feedback_filename) {
    await uploadWithdrawalFileRaw(id, "vision_feedback", record.vision_feedback_blob, record.vision_feedback_filename).catch(() => undefined);
  }
  if (record.live_training_blob && record.live_training_filename) {
    await uploadWithdrawalFileRaw(id, "live_training", record.live_training_blob, record.live_training_filename).catch(() => undefined);
  }
  if (record.debug_image_blob && record.debug_image_filename) {
    await uploadWithdrawalFileRaw(id, "debug", record.debug_image_blob, record.debug_image_filename).catch(() => undefined);
  }
  if (record.debug_data_blob && record.debug_data_filename) {
    await uploadWithdrawalFileRaw(id, "debug_data", record.debug_data_blob, record.debug_data_filename).catch(() => undefined);
  }
}

export interface DraftDiagnosticArtifacts {
  debugImageBlob?: Blob | null;
  debugImageFilename?: string | null;
  debugDataBlob?: Blob | null;
  debugDataFilename?: string | null;
}

export interface DraftDocumentArtifacts {
  pdfBlob: Blob;
  pdfFilename: string;
  pdfPreviewBlob: Blob;
  pdfPreviewFilename: string;
}

/**
 * Zweite Phase des schnellen Draft-Saves. Der Formularstand ist zu diesem
 * Zeitpunkt bereits atomar lokal gespeichert; PDF/WebP dürfen nur dann an
 * genau diese Revision angehängt werden, wenn der Benutzer sie inzwischen
 * nicht erneut geändert oder finalisiert hat.
 */
export async function updateDraftDocumentArtifacts(
  clientUuid: string,
  expectedRevision: number,
  artifacts: DraftDocumentArtifacts,
): Promise<LocalWithdrawalRecord | null> {
  const updated = await updateDraftAtRevision(clientUuid, expectedRevision, (current) => ({
    ...current,
    pdf_blob: artifacts.pdfBlob,
    pdf_filename: artifacts.pdfFilename,
    pdf_preview_blob: artifacts.pdfPreviewBlob,
    pdf_preview_filename: artifacts.pdfPreviewFilename,
    document_artifact_version: WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION,
    document_server_refresh_required: true,
    sync_status: "pending",
    synced_at: null,
    next_sync_attempt_at: null,
    last_error: null,
    updated_at: new Date().toISOString(),
  }));
  if (!updated || updated.record_type !== "draft") return updated;
  if (Math.max(0, updated.content_revision ?? 0) !== Math.max(0, expectedRevision)) return updated;
  dispatchLocalWithdrawalUpdated(updated.client_uuid, updated.content_revision, "draft_pending");
  void registerMaterialentnahmeBackgroundSync();
  void updateMaterialentnahmeAppBadge(1);
  return updated;
}

/**
 * Aktualisiert ausschließlich optionale Diagnoseartefakte eines Drafts.
 * Diese Daten sind kein fachlicher Inhalt: Sie dürfen daher weder die
 * content_revision erhöhen noch einen bereits synchronisierten Draft wieder
 * auf pending setzen oder einen zweiten vollständigen Draft-Sync erzwingen.
 */
export async function updateDraftDiagnostics(
  clientUuid: string,
  artifacts: DraftDiagnosticArtifacts,
  expectedRevision?: number,
): Promise<void> {
  const current = await getLocalWithdrawal(clientUuid);
  if (!current || current.record_type !== "draft") return;
  const revision = expectedRevision ?? Math.max(0, current.content_revision ?? 0);
  const updated = await updateDraftAtRevision(clientUuid, revision, (latest) => ({
    ...latest,
    debug_image_blob: artifacts.debugImageBlob ?? latest.debug_image_blob ?? null,
    debug_image_filename: artifacts.debugImageFilename ?? latest.debug_image_filename ?? null,
    debug_data_blob: artifacts.debugDataBlob ?? latest.debug_data_blob ?? null,
    debug_data_filename: artifacts.debugDataFilename ?? latest.debug_data_filename ?? null,
  }));
  if (!updated || updated.record_type !== "draft") return;
  if (Math.max(0, updated.content_revision ?? 0) !== Math.max(0, revision)) return;

  // Wenn der fachliche Draft bereits eine Server-ID besitzt, können reine
  // Diagnosedateien unabhängig best-effort nachgeladen werden. Scheitert das,
  // bleibt der fachliche Draft trotzdem synchronisiert.
  if (!updated.server_withdrawal_id || typeof navigator === "undefined" || !navigator.onLine) return;
  const id = updated.server_withdrawal_id;
  if (artifacts.debugImageBlob && artifacts.debugImageFilename) {
    await uploadWithdrawalFileRaw(id, "debug", artifacts.debugImageBlob, artifacts.debugImageFilename).catch(() => undefined);
  }
  if (artifacts.debugDataBlob && artifacts.debugDataFilename) {
    await uploadWithdrawalFileRaw(id, "debug_data", artifacts.debugDataBlob, artifacts.debugDataFilename).catch(() => undefined);
  }
}

async function completeDraftSyncOnServer(clientUuid: string): Promise<{ synced_at: string }> {
  const response = await fetchWithTimeout(`/api/withdrawals/drafts/${encodeURIComponent(clientUuid)}/sync-complete`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  }, 5000);
  if (!response.ok) {
    const detail = await response.json().catch(() => null) as { detail?: string } | null;
    throw new HttpError(response.status, detail?.detail ?? `Entwurf konnte serverseitig nicht als synchronisiert bestätigt werden (${response.status}).`);
  }
  return response.json() as Promise<{ synced_at: string }>;
}

async function postDraft(record: LocalWithdrawalRecord): Promise<LocalWithdrawalRecord> {
  const sourceRevision = Math.max(0, record.content_revision ?? 0);
  const beforeRequest = await getLocalWithdrawal(record.client_uuid);
  if (beforeRequest && beforeRequest.record_type !== "draft") return beforeRequest;
  if (beforeRequest && Math.max(0, beforeRequest.content_revision ?? 0) !== sourceRevision) return beforeRequest;

  const response = await fetchWithTimeout("/api/withdrawals/drafts", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      ...record.data,
      client_uuid: record.client_uuid,
      requested_withdrawal_number: record.local_withdrawal_number,
    }),
  }, 5000);
  if (!response.ok) throw new HttpError(response.status, `Entwurf konnte nicht synchronisiert werden (${response.status}).`);
  const result = await response.json() as { id:number; withdrawal_number:number|null; user_id:number; owner_username_snapshot?:string|null };
  if (result.user_id !== record.owner_user_id) throw new Error("Server hat den Entwurf einem anderen Benutzer zugeordnet.");
  rememberConfirmedWithdrawalNumber(record.owner_user_id, result.withdrawal_number);

  let latest = await getLocalWithdrawal(record.client_uuid);
  if (latest && latest.record_type !== "draft") return latest;
  if (!latest) latest = record;

  const identity = {
    server_withdrawal_id: result.id,
    server_withdrawal_number: result.withdrawal_number,
    local_withdrawal_number: result.withdrawal_number ?? latest.local_withdrawal_number,
  };

  // Während des POST kann der Benutzer denselben Draft bereits erneut gespeichert
  // haben. Dann nur die inzwischen reservierte Server-Identität in die NEUESTE
  // Revision übernehmen und genau diese Revision wieder pending lassen. Niemals
  // den älteren fachlichen Snapshot oder dessen PDF/Kontierung zurückschreiben.
  if (Math.max(0, latest.content_revision ?? 0) !== sourceRevision) {
    const advanced = await updateDraftAtRevision(
      record.client_uuid,
      Math.max(0, latest.content_revision ?? 0),
      (current) => ({
        ...current,
        ...identity,
        sync_status: "pending",
        synced_at: null,
        next_sync_attempt_at: null,
        last_error: null,
      }),
    ) ?? latest;
    dispatchLocalWithdrawalUpdated(advanced.client_uuid, advanced.content_revision, "draft_pending");
    void registerMaterialentnahmeBackgroundSync();
    return advanced;
  }

  // Server-Identität sofort persistieren, den Draft aber erst NACH allen
  // vorhandenen Artefakten als synchronisiert markieren. So geht bei einem
  // einzelnen Uploadfehler weder die SID/#ID verloren noch bleibt ein
  // unvollständiger Draft fälschlich dauerhaft auf „Synchronisiert“.
  let updated = await updateDraftAtRevision(record.client_uuid, sourceRevision, (current) => ({
    ...current,
    ...identity,
    sync_status: "syncing",
    last_error: null,
  })) ?? latest;
  if (updated.record_type !== "draft" || Math.max(0, updated.content_revision ?? 0) !== sourceRevision) return updated;
  dispatchLocalWithdrawalUpdated(updated.client_uuid, updated.content_revision, "draft_syncing");
  await uploadDraftArtifacts(updated);

  latest = await getLocalWithdrawal(record.client_uuid);
  if (latest && latest.record_type !== "draft") return latest;
  if (latest && Math.max(0, latest.content_revision ?? 0) !== sourceRevision) {
    const advanced = await updateDraftAtRevision(
      record.client_uuid,
      Math.max(0, latest.content_revision ?? 0),
      (current) => ({
        ...current,
        ...identity,
        sync_status: "pending",
        synced_at: null,
        next_sync_attempt_at: null,
        last_error: null,
      }),
    ) ?? latest;
    dispatchLocalWithdrawalUpdated(advanced.client_uuid, advanced.content_revision, "draft_pending");
    void registerMaterialentnahmeBackgroundSync();
    return advanced;
  }

  const serverCompletion = await completeDraftSyncOnServer(record.client_uuid);

  // Auch während der serverseitigen Abschlussquittierung kann eine neuere lokale
  // Revision entstehen. In diesem Fall bleibt ausschließlich die neueste Revision
  // pending; die alte Antwort darf deren PDF/WebP oder Syncstatus nicht überschreiben.
  latest = await getLocalWithdrawal(record.client_uuid);
  if (latest && latest.record_type !== "draft") return latest;
  if (latest && Math.max(0, latest.content_revision ?? 0) !== sourceRevision) {
    const advanced = await updateDraftAtRevision(
      record.client_uuid,
      Math.max(0, latest.content_revision ?? 0),
      (current) => ({
        ...current,
        ...identity,
        sync_status: "pending",
        synced_at: null,
        next_sync_attempt_at: null,
        last_error: null,
      }),
    ) ?? latest;
    dispatchLocalWithdrawalUpdated(advanced.client_uuid, advanced.content_revision, "draft_pending");
    void registerMaterialentnahmeBackgroundSync();
    return advanced;
  }

  updated = await updateDraftAtRevision(record.client_uuid, sourceRevision, (current) => ({
    ...current,
    ...identity,
    sync_status: "synced",
    synced_at: serverCompletion.synced_at,
    sync_attempt_count: 0,
    document_server_refresh_required: false,
    next_sync_attempt_at: null,
    last_error: null,
  })) ?? (latest ?? updated);
  if (updated.record_type !== "draft" || Math.max(0, updated.content_revision ?? 0) !== sourceRevision) return updated;
  dispatchLocalWithdrawalUpdated(updated.client_uuid, updated.content_revision, "draft_synced");
  void verifyServerArchiveAndCompact(updated.client_uuid).catch(() => 0);
  return updated;
}

export async function saveDraftOfflineFirst(clientUuid: string, data: LocalWithdrawalData, artifacts?: DraftArtifacts, options: { sync?: boolean; preferredWithdrawalNumber?: number | null; documentsPending?: boolean; deferBackgroundSync?: boolean } = {}): Promise<DraftSaveResult> {
  const owner = getLastKnownAuthUser();
  if (!owner) throw new Error("Für den Entwurf ist kein angemeldeter Benutzer bekannt.");
  const existing = await getLocalWithdrawal(clientUuid);
  if (existing && existing.owner_user_id !== owner.id) throw new Error("Dieser lokale Entwurf gehört zu einem anderen Benutzer.");
  // Ein später fertiggestellter Vorgang darf von einer noch laufenden
  // Draft-Hintergrundaufgabe niemals wieder zum Entwurf zurückgeschrieben werden.
  if (existing && existing.record_type !== "draft") {
    return { record: existing, synced: existing.sync_status === "synced" };
  }

  const expectedRevision = Math.max(0, existing?.content_revision ?? 0);
  const reservedLocalNumber = existing?.local_withdrawal_number
    ?? options.preferredWithdrawalNumber
    ?? await nextLocalWithdrawalNumber(owner.id);
  const now = new Date().toISOString();

  const record = await compareAndSwapLocalWithdrawal(clientUuid, expectedRevision, (current) => {
    if (current && current.owner_user_id !== owner.id) {
      throw new Error("Dieser lokale Entwurf gehört zu einem anderen Benutzer.");
    }
    if (current && current.record_type !== "draft") return current;

    const localNumber = current?.local_withdrawal_number ?? reservedLocalNumber;
    return {
      ...(current ?? {}),
      client_uuid: clientUuid,
      record_type: "draft",
      owner_user_id: owner.id,
      owner_username: owner.username,
      local_withdrawal_number: localNumber,
      data: { ...data, client_uuid: clientUuid },
      // Bereits erzeugte Artefakte niemals durch einen späteren Draft-Save verlieren.
      // Neue Artefakte werden vor jedem Serverkontakt zuerst in IndexedDB persistiert.
      pdf_blob: options.documentsPending ? null : (artifacts?.pdfBlob ?? current?.pdf_blob ?? null),
      pdf_filename: options.documentsPending ? null : (artifacts?.pdfFilename ?? current?.pdf_filename ?? null),
      pdf_preview_blob: options.documentsPending ? null : (artifacts?.pdfPreviewBlob ?? current?.pdf_preview_blob ?? null),
      pdf_preview_filename: options.documentsPending ? null : (artifacts?.pdfPreviewFilename ?? current?.pdf_preview_filename ?? null),
      label_blob: artifacts?.labelBlob ?? current?.label_blob ?? null,
      label_filename: artifacts?.labelFilename ?? current?.label_filename ?? null,
      source_blob: artifacts?.sourceBlob ?? current?.source_blob ?? null,
      source_filename: artifacts?.sourceFilename ?? current?.source_filename ?? null,
      detection_source_blob: artifacts?.detectionSourceBlob ?? current?.detection_source_blob ?? null,
      detection_source_filename: artifacts?.detectionSourceFilename ?? current?.detection_source_filename ?? null,
      analysis_input_blob: artifacts?.analysisInputBlob ?? current?.analysis_input_blob ?? null,
      analysis_input_filename: artifacts?.analysisInputFilename ?? current?.analysis_input_filename ?? null,
      rectification_input_blob: artifacts?.rectificationInputBlob ?? current?.rectification_input_blob ?? null,
      rectification_input_filename: artifacts?.rectificationInputFilename ?? current?.rectification_input_filename ?? null,
      ocr_warp_blob: artifacts?.ocrWarpBlob ?? current?.ocr_warp_blob ?? null,
      ocr_warp_filename: artifacts?.ocrWarpFilename ?? current?.ocr_warp_filename ?? null,
      vision_feedback_blob: artifacts?.visionFeedbackBlob ?? current?.vision_feedback_blob ?? null,
      vision_feedback_filename: artifacts?.visionFeedbackFilename ?? current?.vision_feedback_filename ?? null,
      live_training_blob: artifacts?.liveTrainingBlob ?? current?.live_training_blob ?? null,
      live_training_filename: artifacts?.liveTrainingFilename ?? current?.live_training_filename ?? null,
      debug_image_blob: artifacts?.debugImageBlob ?? current?.debug_image_blob ?? null,
      debug_image_filename: artifacts?.debugImageFilename ?? current?.debug_image_filename ?? null,
      debug_data_blob: artifacts?.debugDataBlob ?? current?.debug_data_blob ?? null,
      debug_data_filename: artifacts?.debugDataFilename ?? current?.debug_data_filename ?? null,
      sync_status: "pending",
      server_withdrawal_id: current?.server_withdrawal_id ?? null,
      server_withdrawal_number: current?.server_withdrawal_number ?? null,
      base_version: current?.base_version ?? null,
      created_at: current?.created_at ?? now,
      updated_at: now,
      // Fachlicher Window-Write: genau eine Revision weiter. Der Vergleich mit
      // expectedRevision läuft in derselben IndexedDB-readwrite-Transaktion.
      content_revision: expectedRevision + 1,
      document_artifact_version: options.documentsPending
        ? null
        : artifacts?.pdfBlob && artifacts?.pdfPreviewBlob ? WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION : (current?.document_artifact_version ?? null),
      document_server_refresh_required: options.documentsPending
        ? true
        : artifacts?.pdfBlob && artifacts?.pdfPreviewBlob ? true : (current?.document_server_refresh_required ?? false),
      last_sync_attempt_at: null,
      sync_attempt_count: current?.sync_attempt_count ?? 0,
      next_sync_attempt_at: null,
      // Jede neue fachliche Revision ist bis zur erneuten Serverbestätigung
      // bewusst nicht mehr synchronisiert. Der vorherige Synczeitpunkt darf nicht
      // als Quittierung für die gerade geänderte PDF/WebP weiterleben.
      synced_at: null,
      last_error: null,
      sync_lease_owner: current?.sync_lease_owner ?? null,
      sync_lease_expires_at: current?.sync_lease_expires_at ?? null,
      sync_lease_updated_at: current?.sync_lease_updated_at ?? null,
      sync_phase: current?.sync_phase ?? null,
      last_successful_sync_phase: current?.last_successful_sync_phase ?? null,
      sync_failure_phase: current?.sync_failure_phase ?? null,
      sync_failure_detail: current?.sync_failure_detail ?? null,
      sync_phase_updated_at: current?.sync_phase_updated_at ?? null,
      sync_trace: current?.sync_trace ?? [],
    } satisfies LocalWithdrawalRecord;
  });

  if (!record) throw new Error("Lokaler Entwurf konnte nicht atomar gespeichert werden.");
  // Falls während der Reservierung bereits finalisiert wurde, gibt CAS den
  // aktuellen Nicht-Draft zurück statt ihn wieder in einen Draft zu verwandeln.
  if (record.record_type !== "draft") {
    return { record, synced: record.sync_status === "synced" };
  }

  dispatchLocalWithdrawalUpdated(record.client_uuid, record.content_revision, "draft_saved");
  if (!options.deferBackgroundSync) void registerMaterialentnahmeBackgroundSync();
  void updateMaterialentnahmeAppBadge(1);
  if (options.sync !== false && typeof navigator !== "undefined" && navigator.onLine) {
    const latest = await getLocalWithdrawal(clientUuid);
    if (latest && latest.record_type !== "draft") return { record: latest, synced: latest.sync_status === "synced" };
    try {
      const syncedRecord = await syncLocalDraft(latest ?? record);
      return { record: syncedRecord, synced: syncedRecord.sync_status === "synced" };
    }
    catch {
      const current = await getLocalWithdrawal(clientUuid);
      if (current && current.record_type !== "draft") return { record: current, synced: current.sync_status === "synced" };
      return { record: current ?? record, synced: false };
    }
  }
  return { record, synced: false };
}

async function syncLocalDraftUnlocked(record: LocalWithdrawalRecord): Promise<LocalWithdrawalRecord> {
  if (record.record_type !== "draft") return record;
  const owner = getLastKnownAuthUser();
  if (!owner || owner.id !== record.owner_user_id) return record;

  let current = await getLocalWithdrawal(record.client_uuid) ?? record;
  if (current.record_type !== "draft") return current;
  current = await ensureDraftDocumentsBeforeSync(current);
  if (current.record_type !== "draft") return current;
  const previousAttempts = Math.min(current.sync_attempt_count ?? 0, DRAFT_SYNC_MAX_ATTEMPTS);
  if (previousAttempts >= DRAFT_SYNC_MAX_ATTEMPTS) return current;

  const attempt = previousAttempts + 1;
  const sourceRevision = Math.max(0, current.content_revision ?? 0);
  current = await updateDraftAtRevision(record.client_uuid, sourceRevision, (latest) => ({
    ...latest,
    sync_status: "syncing",
    sync_attempt_count: attempt,
    last_sync_attempt_at: new Date().toISOString(),
    next_sync_attempt_at: null,
    last_error: null,
  })) ?? current;
  if (current.record_type !== "draft" || Math.max(0, current.content_revision ?? 0) !== sourceRevision) return current;
  dispatchLocalWithdrawalUpdated(current.client_uuid, current.content_revision, "draft_syncing");

  try {
    const synced = await postDraft(current);
    return { ...synced, sync_attempt_count: 0, next_sync_attempt_at: null };
  } catch (error) {
    const latest = await getLocalWithdrawal(record.client_uuid) ?? current;
    if (latest.record_type !== "draft") return latest;
    if (Math.max(0, latest.content_revision ?? 0) !== Math.max(0, current.content_revision ?? 0)) {
      const advanced = await updateDraftAtRevision(
        record.client_uuid,
        Math.max(0, latest.content_revision ?? 0),
        (fresh) => ({
          ...fresh,
          sync_status: "pending",
          synced_at: null,
          next_sync_attempt_at: null,
          last_error: null,
        }),
      ) ?? latest;
      dispatchLocalWithdrawalUpdated(advanced.client_uuid, advanced.content_revision, "draft_pending");
      void registerMaterialentnahmeBackgroundSync();
      return advanced;
    }
    const connectivity = isDraftConnectivityError(error);
    const terminal = isTerminalDraftError(error);
    const effectiveAttempts = connectivity ? previousAttempts : attempt;
    const failed = await updateDraftAtRevision(record.client_uuid, sourceRevision, (fresh) => ({
      ...fresh,
      sync_status: connectivity ? "pending" : "error",
      sync_attempt_count: effectiveAttempts,
      next_sync_attempt_at: !connectivity && !terminal && effectiveAttempts < DRAFT_SYNC_MAX_ATTEMPTS
        ? new Date(Date.now() + draftRetryDelayMs(effectiveAttempts)).toISOString()
        : null,
      last_error: connectivity
        ? draftConnectivityMessage(error)
        : error instanceof HttpError && error.status === 409
          ? `Bearbeitungskonflikt · ${error.message}`
          : error instanceof Error ? error.message : "Entwurf-Synchronisierung fehlgeschlagen.",
    })) ?? latest;
    if (failed.record_type !== "draft" || Math.max(0, failed.content_revision ?? 0) !== sourceRevision) {
      if (failed.record_type === "draft") {
        dispatchLocalWithdrawalUpdated(failed.client_uuid, failed.content_revision, "draft_pending");
        void registerMaterialentnahmeBackgroundSync();
      }
      return failed;
    }
    dispatchLocalWithdrawalUpdated(failed.client_uuid, failed.content_revision, connectivity ? "draft_pending" : "draft_error");
    void registerMaterialentnahmeBackgroundSync();
    throw error;
  }
}

export async function syncLocalDraft(record: LocalWithdrawalRecord): Promise<LocalWithdrawalRecord> {
  const existing = activeDraftSyncs.get(record.client_uuid);
  if (existing) return existing;

  const leaseOwner = createLocalSyncLeaseOwner("window");
  const leased = await acquireLocalWithdrawalSyncLease(record.client_uuid, leaseOwner);
  if (!leased) return await getLocalWithdrawal(record.client_uuid) ?? record;

  const heartbeat = window.setInterval(() => {
    void renewLocalWithdrawalSyncLease(record.client_uuid, leaseOwner).then((renewed) => {
      if (!renewed) console.warn(`Draft-Sync-Lease für ${record.client_uuid} konnte nicht verlängert werden.`);
    }).catch(() => undefined);
  }, DRAFT_SYNC_LEASE_HEARTBEAT_MS);

  const operation = syncLocalDraftUnlocked(leased).finally(async () => {
    window.clearInterval(heartbeat);
    await releaseLocalWithdrawalSyncLease(record.client_uuid, leaseOwner).catch(() => undefined);
    if (activeDraftSyncs.get(record.client_uuid) === operation) activeDraftSyncs.delete(record.client_uuid);
  });
  activeDraftSyncs.set(record.client_uuid, operation);
  return operation;
}
